import React, { StrictMode } from 'react'
import { useTranslation } from 'react-i18next'
import './i18n'
import styled, { ThemeProvider } from 'styled-components'
import { gsap, TweenLite, Quad } from 'gsap'
import { CSSRulePlugin } from 'gsap/CSSRulePlugin'
import GD from './GD'
import { DefaultValues } from './enums'
import { getTheme } from './Theme'
import Header from './containers/Header'
import GuestNotice from './containers/GuestNotice'
import Input from './containers/Input'
import MessageList from './containers/MessageList'
import UploadIndicator from './containers/UploadIndicator'
import Alert from './containers/Alert'
import TypingIndicator from './containers/TypingIndicator'
import Version from './containers/Version'
import { Config } from './managers'

gsap.registerPlugin(CSSRulePlugin)

let startWindowRPosition = 25
let startWindowBPosition = 25

const ChatWindowBase = styled.div`
  bottom: ${startWindowBPosition}px;
  right: ${startWindowRPosition}px;
`
const Root = styled(ChatWindowBase)`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 320px;
  height: 637px;
  background-color: ${props => props.theme.color.main.background};
  border-radius: 30px;
  overflow: hidden;
  font-size: ${props => props.theme.fontSize.chat};
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  line-height: normal !important;
  text-align: left;
  transition: visibility 0.3s, opacity 0.3s;

  &[data-mobile=true] {
    position: fixed;
    top: 0;
    left: 0;
    bottom: auto;
    right: auto;
    border-radius: 0;
    width: 100%;
    height: 100vh; /* фолбэк для браузеров без поддержки dvh */
    height: 100dvh; /* учитывает нативную панель браузера, чтобы она не перекрывала инпут */
    box-shadow: none;
  }
`
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  // height: 100%;
  overflow-y: auto;
`
const Scroll = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  justify-content: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
`
const ChatBody = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 670px;
  @media (min-width: 1550px) {
    position: relative;
  }
`

let animation: TweenLite | null = null

interface Props {
  sharedData: IShared
}

function App({ sharedData }: Props) {
  const { i18n } = useTranslation()

  let smx = 0
  let smy = 0
  let sx = 0
  let sy = 0

  const ref = React.useRef<HTMLDivElement | null>(null)
  const body = React.useRef<HTMLDivElement | null>(null)
  const arrowRef = React.useRef<HTMLDivElement | null>(null)
  const throttleTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const withoutChatHeader = sharedData.headless

  const openChat = React.useCallback(() => {
    if (!ref.current) return

    ref.current.style.visibility = 'visible'
    ref.current.style.opacity = '1'

    GD.GUI_CHAT_OPENED.invoke()
  }, [ref])

  const closeChat = React.useCallback(() => {
    if (!ref.current) return

    ref.current.style.visibility = 'hidden'
    ref.current.style.opacity = '0'

    GD.GUI_CHAT_CLOSED.invoke()
  }, [ref])

  const scrollToBottom = React.useCallback(() => {
    if (!body.current) return
    body.current.scrollTop = body.current.scrollHeight
  }, [])

  const setScrollPosition = React.useCallback(() => {
    if (!body.current) return

    const arrowDownVisible = arrowRef?.current?.style.display === 'flex'

    if (arrowDownVisible) {
      // Scroll down 50 pixels to detach from top
      body.current.scrollTop += 50
    }
    else {
      // Scroll to the very bottom
      scrollToBottom()
    }
  }, [scrollToBottom])

  const moveBox = React.useCallback((e: MouseEvent) => {
    if (!ref.current) return

    const tx = sx - (e.clientX - smx)
    const ty = sy - (e.clientY - smy)

    ref.current.style.bottom = ty + 'px'
    ref.current.style.right = tx + 'px'
    setupBounds()
  }, [smx, smy, sx, sy])

  const onWindowResize = React.useCallback(() => {
    setupBounds()
  }, [])

  const onMouseMove = React.useCallback((e: MouseEvent) => {
    moveBox(e)
  }, [moveBox])

  const onMouseUp = React.useCallback((e: MouseEvent) => {
    moveBox(e)
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  }, [moveBox, onMouseMove])

  React.useEffect(() => {
    const sd: IShared = sharedData

    if (sd && ref.current) {
      if (Config.japanEps.includes(Number(sd.ep))) {
        void i18n.changeLanguage('ja')
      }

      if (!sd.mobile) {
        ref.current.style.right = startWindowRPosition + 'px'
        ref.current.style.bottom = startWindowBPosition + 'px'
        if (sd.bottomOffset && !isNaN(sd.bottomOffset)) startWindowBPosition = sd.bottomOffset
        if (sd.rightOffset && !isNaN(sd.rightOffset)) startWindowRPosition = sd.rightOffset
      }
      setupBounds()
    }

    GD.CLOSE_CHAT.subscribe(() => {
      closeChat()
    }, 'chat')

    GD.GUI_CLOSE_CHAT.subscribe(() => {
      closeChat()
    }, 'chat')

    GD.GUI_OPEN_CHAT.subscribe(() => {
      openChat()
    }, 'chat')

    GD.GUI_MESSAGES_RENDERED.subscribe(setScrollPosition, 'chat')

    window.addEventListener('resize', onWindowResize)

    GD.REQUEST_AUTHENTICATE.invoke()

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      window.removeEventListener('resize', onWindowResize)
      GD.GUI_MESSAGES_RENDERED.unsubscribe('chat')
      GD.GUI_CLOSE_CHAT.unsubscribe('chat')
      GD.GUI_OPEN_CHAT.unsubscribe('chat')
      GD.CLOSE_CHAT.unsubscribe('chat')
      if (animation) animation.kill()
    }
  }, [closeChat, onMouseMove, onMouseUp, onWindowResize, openChat, setScrollPosition, sharedData])

  const setupBounds = (): void => {
    if (!ref.current) return

    const bounds = ref.current.getBoundingClientRect()
    let tx = parseInt(ref.current.style.right)
    let ty = parseInt(ref.current.style.bottom)

    if (isNaN(tx) || isNaN(ty)) return
    if (tx < startWindowRPosition) tx = startWindowRPosition
    if (ty < startWindowBPosition) ty = startWindowBPosition

    const boundX = window.innerWidth - bounds.width
    const boundY = window.innerHeight - bounds.height
    if (tx > boundX) tx = boundX
    if (ty > boundY) ty = boundY
    ref.current.style.bottom = ty + 'px'
    ref.current.style.right = tx + 'px'
  }

  const onMDown = (e: React.MouseEvent) => {
    if (sharedData.mobile) return

    if (!ref.current) return

    if ((e.target as HTMLElement).id !== 'chatHead') return

    smx = e.clientX
    smy = e.clientY

    const rect = ref.current.getBoundingClientRect()
    sx = rect.left
    sy = rect.top

    sx = parseInt(ref.current.style.right)
    sy = parseInt(ref.current.style.bottom)

    if (isNaN(sx)) {
      sx = startWindowRPosition
      ref.current.style.right = sx + 'px'
    }
    if (isNaN(sy)) {
      sy = startWindowBPosition
      ref.current.style.bottom = sy + 'px'
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const headDoubleClick = () => {
    if (!ref.current) return

    if (ref.current.getAttribute('closing')) return

    if (sharedData.mobile) return

    if (ref.current.getAttribute('expanded')) {
      animation = TweenLite.to(ref.current, 0.2, {
        width: 320,
        height: '50vh',
        bottom: startWindowBPosition + 'px',
        right: startWindowRPosition + 'px',
        ease: Quad.easeOut,
      })
      ref.current.removeAttribute('expanded')
    }
    else {
      animation = TweenLite.to(ref.current, 0.2, {
        width: 390,
        height: `calc(95vh - ${startWindowBPosition}px)`,
        bottom: startWindowBPosition + 'px',
        right: startWindowRPosition + 'px',
        ease: Quad.easeOut,
      })
      ref.current.setAttribute('expanded', '1')
    }
  }

  const handleChatBodyScroll = () => {
    if (throttleTimeout.current) return

    throttleTimeout.current = setTimeout(() => {
      if (body.current && arrowRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = body.current
        const isScrolledUp = scrollHeight - (scrollTop + clientHeight) >= DefaultValues.SCROLL_THRESHOLD
        arrowRef.current.style.display = isScrolledUp ? 'flex' : 'none'
      }

      throttleTimeout.current = null
    }, 500)
  }

  return (
    <StrictMode>
      <ThemeProvider theme={getTheme()}>
        <Root id="DucascopyConnect" ref={ref} onMouseDown={onMDown} data-mobile={sharedData.mobile}>
          <Wrapper>
            {!withoutChatHeader && (
              <Header
                mobile={Boolean(sharedData.mobile)}
                noClose={Boolean(sharedData.noClose)}
                onDoubleClick={headDoubleClick}
              />
            )}
            <GuestNotice />
            <div id="modal-portal" />
            <Scroll onScroll={handleChatBodyScroll} ref={body}>
              <ChatBody>
                <MessageList />
                <TypingIndicator />
              </ChatBody>
            </Scroll>
            <UploadIndicator />
            <Input />
            <Alert />
          </Wrapper>

          <Version />
        </Root>
      </ThemeProvider>
    </StrictMode>
  )
}

export default App